/**
 * ListViewShell — Push 81 (2026-07-23) canonical list-shape primitive.
 * ============================================================================
 * Unifies the header + toolbar + filter + search + view-selector + bulk-actions
 * + empty-state chrome across every list-shape surface in the app (standalone
 * pages AND embedded sections within multi-section pages). Same shape of move
 * as `<RecordDetailLayout>` (Round 6 Item 16): primitive owns chrome, consumer
 * owns semantics.
 *
 * The primitive is variant-driven:
 *   - `variant='standalone'` — page-level chrome density (larger inputs, more
 *     padding). Standalone consumers that already render their own
 *     `<div className="lightning-highlight">` hero above the shell should NOT
 *     pass `title` / `helper` / `count` — pass those through their hero as
 *     before + let the shell render only the toolbar + body. Standalone
 *     consumers that DON'T have a hero should pass `title` + `helper` + `count`
 *     + `icon` and the shell renders a lightning-highlight-shaped header.
 *   - `variant='embedded'` — section-level chrome density. Shell renders a
 *     `.section-title` header + one-line helper + compact toolbar (matching
 *     Push 78 SettingsUsage SpendLimitsByUserSection DNA). No hero.
 *
 * The primitive owns:
 *   - Header block (title + helper + count badge + right-side header actions)
 *     when the consumer passes structured header props. Skippable when the
 *     consumer already renders a hero above.
 *   - Toolbar row (search input with `IconSearch` prefix + optional view
 *     selector + optional filter dropdown + right-aligned header actions
 *     + optional bulk-action sticky bar when `bulkSelect` reports > 0
 *     selected rows).
 *   - Skeleton loading fallback via `SkeletonTable` when `loading=true`.
 *   - Empty-state fallback via `<EmptyState>` when `empty` prop provided AND
 *     children render zero rows (consumer-driven — see docstring for
 *     `emptyStateOverride`).
 *
 * The consumer owns:
 *   - The `<table>` markup (or grid, or `<div>` card-list markup), its columns,
 *     its `<tbody>` rows.
 *   - Row rendering (uses `<ListRow>` companion primitive to compose the
 *     row-click drill + kebab + bulk-select checkbox + interactive-descendant
 *     bail-out).
 *   - Inline-edit cells (uses `<InlineEditableCell>` companion primitive for
 *     dashed-hover-border affordance).
 *
 * Filter + view selector + sort state persistence is per-surface namespaced
 * via the `storageKey` prop (`list-view:{surface-name}` convention). Callers
 * still manage their own React state; the shell just exposes the storage
 * key as a namespace for surface-scoped `localStorage` keys.
 *
 * Search input value is TRANSIENT (per-session; not persisted). Bulk-select
 * state is TRANSIENT.
 *
 * Locked adjudications (OC 2026-07-18):
 *   - Variant naming: `'standalone' | 'embedded'` (chrome density distinction)
 *   - Sort: primitive supports both client + controlled server-side; consumer
 *     picks via `sortMode` prop
 *   - Bulk actions: uniform opt-in support; primitive owns frame, consumer
 *     owns semantic actions
 *   - Storage key: per-surface namespaced (`list-view:{surface-name}`)
 *   - No new D-XXX (primitive evolution consistent with PATTERNS.md § D lock)
 *
 * PATTERNS.md § D canonical implementation reference for the "List view
 * shape (universal, primitives-first)" entry (Push 78 lock, evolved Push 81,
 * refined Push 82).
 *
 * Push 82 refinements (2026-07-23, OC-approved 2026-07-18):
 *   - A.1 Hero-into-shell — standalone consumers pass `icon` + `title` +
 *     `helper` + `headerActions` and the shell renders the lightning-highlight
 *     hero (no more consumer-owned hero above the shell). Push 81 anchors
 *     that already had heroes outside get migrated inline this push.
 *   - A.2 Column-header sort chevrons — `<SortableColumnHeader>` +
 *     `useListViewShellSortState` hook. Click cycle: none → asc → desc →
 *     none. Persists to `${storageKey}:sort`. Companion to the existing
 *     "Sort by:" button-bar (Push 81 mode='controlled'); either shape ships.
 *   - A.3 Active-filter chip row — `filter.activeFacets` prop; shell renders
 *     an inline chip strip below the toolbar with per-facet remove buttons
 *     + "Clear all" link at 2+ facets. Replaces verbose "Filtered by X"
 *     status text (Salesforce anti-pattern).
 *
 * Sub-agent RRRRR (2026-07-31) — parallel/equal-height header unification:
 *   Every list view now renders title + helper on the LEFT and search +
 *   filter funnel on the RIGHT of a SINGLE header row (flex align-items:
 *   center). No second toolbar strip below the title. The preset "pillbox"
 *   viewSelector prop is retired — every preset filter (Active/Inactive,
 *   Success/Failed, All/Pending/Historical) now lives inside the filter
 *   dropdown as a Status dimension. Active-filter chip strip (A.3) remains
 *   as the visible state indicator below the toolbar. Descriptions land
 *   next to titles wherever a list-view surface exists ("use discretion" —
 *   inbox-style views may skip, capability admin views definitely carry
 *   one, sentence case, no fluff).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
  type ThHTMLAttributes,
} from 'react';

import { IconChevron, IconClose, IconSearch } from './Icon.tsx';
import {
  ListFilterDropdown,
  type ListFilterDimension,
} from './ListFilterDropdown.tsx';
import { SkeletonTable } from './Skeleton.tsx';
import { EmptyState, type EmptyStateAction } from './EmptyState.tsx';
import type { ObjectKind } from './ObjectIcon.tsx';
import { ObjectIcon } from './ObjectIcon.tsx';

// ---------------------------------------------------------------------------
// Prop shapes — each shell slot is opt-in.
// ---------------------------------------------------------------------------

export interface ListViewShellSearchConfig {
  /** Raw, uncontrolled search input value (as-typed). */
  value: string;
  /** Fires on every keystroke — parent stores the raw value. */
  onChange: (next: string) => void;
  /** Placeholder for the search input. Default: "Search". */
  placeholder?: string;
  /** aria-label for the search input. Default: matches placeholder. */
  ariaLabel?: string;
  /**
   * Debounce interval (ms) before the shell emits `onDebouncedChange`.
   * Default 200 (matches Push 78 SpendLimitsByUserSection anchor). Consumers
   * that don't need a separate debounced value can omit `onDebouncedChange`
   * — the shell doesn't force a debounce onto the raw path.
   */
  debounceMs?: number;
  /**
   * Optional debounced echo — fires `debounceMs` after the raw value stops
   * changing. Consumers can either subscribe to the raw `value` + do their
   * own debounce, or subscribe to `onDebouncedChange` for the shared 200ms
   * cadence.
   */
  onDebouncedChange?: (next: string) => void;
}

/**
 * Sub-agent RRRRR (2026-07-31) — the `viewSelector` prop shape is RETIRED.
 * Every preset pillbox filter (Active/Inactive, Success/Failed, All/Pending/
 * Historical, etc.) now routes through the unified `filter` prop as a
 * Status/Kind/etc. dimension inside `<ListFilterDropdown>`. Active
 * selections surface via the `filter.activeFacets` chip strip below the
 * toolbar (Push 82 refinement A.3). If a legacy consumer still passes
 * `viewSelector`, the shell silently ignores it — the caller must migrate.
 */

/**
 * Push 82 (2026-07-23) refinement A.3 — active-filter chip row. When the
 * consumer computes the list of currently-selected facets, the shell renders
 * an inline chip strip below the toolbar row: `Role: Admin ×  Tag: seasonal ×`.
 * Click × to remove that facet (calls `onRemove`). When 2+ facets are active,
 * shell appends a "Clear all" link that fires `onReset`.
 */
export interface ListViewShellActiveFacet {
  /** Composite key for React reconciliation (e.g. "role:admin"). */
  key: string;
  /** Human-readable label rendered inside the chip (e.g. "Role: Admin"). */
  label: string;
  /** Fires when the user clicks the × on the chip. */
  onRemove: () => void;
}

export interface ListViewShellFilterConfig {
  /** Number of active filters — drives the trigger's active dot. */
  activeCount: number;
  /** Dimension list passed straight to `<ListFilterDropdown>`. */
  dimensions: ListFilterDimension[];
  onReset?: () => void;
  /** aria-label for the filter trigger. */
  ariaLabel?: string;
  /** Portal menu id — pass a stable string so multiple dropdowns can co-render. */
  menuId?: string;
  /**
   * Push 82 (2026-07-23) refinement A.3 — active facets rendered as inline
   * chip strip below the toolbar. Omit or pass an empty array to skip the
   * chip strip. When set + `activeFacets.length >= 2`, shell appends a
   * "Clear all" link that fires `onReset` (which the consumer already wires).
   */
  activeFacets?: ListViewShellActiveFacet[];
}

export interface ListViewShellSortColumn<TId extends string = string> {
  id: TId;
  label: string;
}

export interface ListViewShellSortActive<TId extends string = string> {
  columnId: TId;
  direction: 'asc' | 'desc';
}

export interface ListViewShellSortConfig<TId extends string = string> {
  /**
   * `'client'` = shell is informational only; parent does the sort in its
   * own `useMemo`.
   * `'controlled'` = shell exposes `activeSort` + `onSortChange` for
   * server-driven / controlled sort. Parent updates the fetch on change.
   */
  mode: 'client' | 'controlled';
  columns: ListViewShellSortColumn<TId>[];
  activeSort?: ListViewShellSortActive<TId>;
  onSortChange?: (next: ListViewShellSortActive<TId>) => void;
}

export interface ListViewShellBulkAction {
  label: string;
  onClick: () => void;
  /** Renders in danger styling (matches People bulk deactivate). */
  danger?: boolean;
  /** Grays out the action when true. */
  disabled?: boolean;
}

export interface ListViewShellBulkSelectConfig {
  selectedIds: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  actions: ListViewShellBulkAction[];
  /** Optional muted status line (e.g. "3 of 12 done…") right of the count. */
  progressCaption?: string;
  /** Optional inline extra content to render in the bulk bar (e.g. a "assign
   *  to PS" picker). Rendered between the selection count + the actions. */
  extraContent?: ReactNode;
}

export interface ListViewShellEmptyConfig {
  kind?: ObjectKind;
  title: string;
  body?: string;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
}

// ---------------------------------------------------------------------------
// ListViewShell — the primitive.
// ---------------------------------------------------------------------------

export interface ListViewShellProps {
  /** Chrome density distinction. See file docstring. */
  variant: 'standalone' | 'embedded';
  /**
   * Per-surface localStorage namespace — REQUIRED. Convention:
   * `list-view:{surface-name}` (e.g. `list-view:usage-spend-limits`,
   * `list-view:admin-people`, `list-view:admin-library`).
   *
   * Enforcement note: the shell does NOT itself write to localStorage — that
   * remains the caller's job so surface-specific serializations (JSON blobs,
   * multi-key shapes, etc.) can stay caller-owned. `storageKey` documents
   * the namespace the caller is using; the shell asserts (dev-only console
   * warning) that the key follows the `list-view:` prefix so surfaces don't
   * silently collide.
   */
  storageKey: string;

  // Header (all optional — consumer can render their own hero above shell).
  /** Section / page title. Optional — omit when the consumer's own hero
   *  already carries the title above the shell. */
  title?: string;
  /**
   * One-line helper text below the title. Accepts ReactNode so capability
   * pillar pages can inline a link ("Then <Link>assign</Link> to your team")
   * without losing the primitive-owned header shape. Plain strings still
   * render the same as before.
   */
  helper?: ReactNode;
  /**
   * Optional result-count badge next to the title. Renders as "{count} items"
   * or "{count} of {total}" when filtered narrower than the full set.
   */
  count?: number;
  /** Optional total (unfiltered) count. When `count` < `total` the badge
   *  reads "{count} of {total}". */
  total?: number;
  /** Optional ObjectIcon slot for standalone variant with lightning-highlight
   *  chrome. When present + variant='standalone', renders the lightning-
   *  highlight shape (icon + title + helper + right-side headerActions). */
  icon?: ObjectKind;
  /**
   * Right-aligned header actions (e.g. "+ Add" / "Upload" primary CTAs).
   * Rendered inside the header block (lightning-highlight right cluster for
   * standalone + icon; header row right cluster for embedded).
   */
  headerActions?: ReactNode;
  /**
   * Extra toolbar-row actions rendered in the toolbar's right cluster
   * (alongside search + filter). Use for surface-scoped CTAs that logically
   * belong to the table controls, not the page hero — e.g. People's "Set
   * caps by tag" button that operates on the roster.
   */
  toolbarActions?: ReactNode;

  // Toolbar.
  search?: ListViewShellSearchConfig;
  filter?: ListViewShellFilterConfig;
  sort?: ListViewShellSortConfig;
  bulkSelect?: ListViewShellBulkSelectConfig;

  // Body.
  /** Consumer's table body (or grid, or card-list markup). */
  children: ReactNode;
  /** Optional empty-state config — rendered when children is null OR
   *  `hasNoRows=true` is passed. */
  empty?: ListViewShellEmptyConfig;
  /** Signal from the consumer that the current dataset is empty (post-filter).
   *  When true AND `empty` is provided, shell renders the EmptyState in place
   *  of children. */
  hasNoRows?: boolean;
  /** Renders a `<SkeletonTable>` skeleton in place of children. */
  loading?: boolean;
  /**
   * Skeleton table dims — number of rows + columns. Callers with wider
   * tables should pass matching cols so the skeleton width matches the
   * eventual rendered width. Default: rows=5, cols=4.
   */
  skeletonRows?: number;
  skeletonCols?: number;
  /**
   * When set, callers override the shell's default empty rendering with a
   * consumer-provided empty state. Useful for surfaces that show a
   * combined "no results match filter" caption inside the table body
   * itself (colspan pattern in People.tsx) — those consumers should pass
   * their own `hasNoRows=false` + skip `empty`, letting the table body
   * carry the message.
   */
  emptyStateOverride?: ReactNode;
}

export function ListViewShell({
  variant,
  storageKey,
  title,
  helper,
  count,
  total,
  icon,
  headerActions,
  toolbarActions,
  search,
  filter,
  sort,
  bulkSelect,
  children,
  empty,
  hasNoRows = false,
  loading = false,
  skeletonRows = 5,
  skeletonCols = 4,
  emptyStateOverride,
}: ListViewShellProps) {
  // Dev-only nudge if the storageKey doesn't follow the surface-scoped
  // convention. Not a hard error — some callers may pre-date the rename.
  if (
    typeof window !== 'undefined' &&
    !storageKey.startsWith('list-view:') &&
    typeof (globalThis as { __olyticListViewShellWarn?: boolean })
      .__olyticListViewShellWarn === 'undefined'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ListViewShell] storageKey "${storageKey}" should follow the "list-view:{surface-name}" convention (Push 81 primitive lock).`,
    );
    (globalThis as { __olyticListViewShellWarn?: boolean }).__olyticListViewShellWarn = true;
  }

  // Debounced search echo. Only wired when the consumer subscribes.
  useEffect(() => {
    if (search?.onDebouncedChange === undefined) return;
    const debounceMs = search.debounceMs ?? 200;
    const value = search.value;
    const emit = search.onDebouncedChange;
    const t = window.setTimeout(() => emit(value), debounceMs);
    return () => window.clearTimeout(t);
  }, [search?.value, search?.debounceMs, search?.onDebouncedChange]);

  // Which portion of the shell renders? When `loading` is true, skeleton
  // takes the body slot. Empty state (if opted-in) takes body when
  // `hasNoRows`. Otherwise children.
  const bodyContent: ReactNode = (() => {
    if (loading) return <SkeletonTable rows={skeletonRows} cols={skeletonCols} />;
    if (hasNoRows) {
      if (emptyStateOverride !== undefined) return emptyStateOverride;
      if (empty !== undefined) {
        return (
          <div style={{ padding: variant === 'embedded' ? 12 : 24 }}>
            <EmptyState
              {...(empty.kind !== undefined ? { kind: empty.kind } : {})}
              title={empty.title}
              {...(empty.body !== undefined ? { body: empty.body } : {})}
              {...(empty.primaryAction !== undefined
                ? { primaryAction: empty.primaryAction }
                : {})}
              {...(empty.secondaryAction !== undefined
                ? { secondaryAction: empty.secondaryAction }
                : {})}
              variant="inline"
            />
          </div>
        );
      }
    }
    return children;
  })();

  // Whether the shell renders its own header block (title / helper / icon).
  // Skippable when the consumer's own hero above the shell already carries
  // the title.
  const hasHeader =
    title !== undefined || helper !== undefined || headerActions !== undefined;

  // Sub-agent RRRRR (2026-07-31) — the header + toolbar rows are now
  // MERGED into a single parallel row: left = icon/title/helper, right =
  // headerActions + search + filter (+ toolbarActions). flex align-items:
  // center keeps both sides vertically aligned regardless of description
  // length. When the consumer skips title/helper (e.g. hero lives above
  // the shell), we still render the same one-row toolbar shape on the
  // right so the shell body starts at a consistent baseline.
  const hasToolbarControls =
    search !== undefined ||
    filter !== undefined ||
    toolbarActions !== undefined;
  const hasAnyHeaderRow = hasHeader || hasToolbarControls;

  // Compose the right-cluster once and hand it to both variants. The
  // header primitive wraps this in its own flex container (either
  // `.lightning-highlight-actions` for standalone+icon, or an inline
  // flex row for embedded / standalone-without-icon).
  const rightClusterHasContent =
    hasToolbarControls || headerActions !== undefined;
  const rightCluster: ReactNode = rightClusterHasContent ? (
    <>
      {search !== undefined && <ListViewShellSearchInput search={search} />}
      {filter !== undefined && (
        <ListFilterDropdown
          activeCount={filter.activeCount}
          dimensions={filter.dimensions}
          {...(filter.onReset !== undefined ? { onReset: filter.onReset } : {})}
          {...(filter.ariaLabel !== undefined ? { ariaLabel: filter.ariaLabel } : {})}
          {...(filter.menuId !== undefined ? { menuId: filter.menuId } : {})}
        />
      )}
      {toolbarActions !== undefined && toolbarActions}
      {headerActions !== undefined && headerActions}
    </>
  ) : null;

  return (
    <div
      className={`list-view-shell list-view-shell--${variant}`}
      data-storage-key={storageKey}
    >
      {hasAnyHeaderRow && (
        <ListViewShellHeader
          variant={variant}
          {...(title !== undefined ? { title } : {})}
          {...(helper !== undefined ? { helper } : {})}
          {...(count !== undefined ? { count } : {})}
          {...(total !== undefined ? { total } : {})}
          {...(icon !== undefined ? { icon } : {})}
          rightCluster={rightCluster}
        />
      )}

      {filter?.activeFacets !== undefined && filter.activeFacets.length > 0 && (
        <ListViewShellActiveFacetsRow
          facets={filter.activeFacets}
          {...(filter.onReset !== undefined ? { onReset: filter.onReset } : {})}
        />
      )}

      {bulkSelect !== undefined && bulkSelect.selectedIds.size > 0 && (
        <ListViewShellBulkBar bulkSelect={bulkSelect} variant={variant} />
      )}

      {sort !== undefined && sort.mode === 'controlled' && (
        <ListViewShellSortControls sort={sort} />
      )}

      {bodyContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-primitives
// ---------------------------------------------------------------------------

interface ListViewShellHeaderProps {
  variant: 'standalone' | 'embedded';
  title?: string;
  helper?: ReactNode;
  count?: number;
  total?: number;
  icon?: ObjectKind;
  /**
   * Sub-agent RRRRR (2026-07-31) — merged right cluster (search + filter +
   * toolbarActions + headerActions). The shell composes this once and hands
   * it to the header primitive so BOTH standalone and embedded variants
   * render title/helper on the left and the same controls on the right of
   * a single parallel row (flex align-items: center).
   */
  rightCluster: ReactNode;
}

function ListViewShellHeader({
  variant,
  title,
  helper,
  count,
  total,
  icon,
  rightCluster,
}: ListViewShellHeaderProps) {
  // Standalone + icon → lightning-highlight-shaped hero. Left cluster
  // (icon + title + helper) sits alongside the right cluster (search +
  // filter + CTAs) vertically centered inside the hero card.
  if (variant === 'standalone' && icon !== undefined) {
    return (
      <div
        className="lightning-highlight"
        style={{ alignItems: 'center' }}
      >
        <div className="lightning-highlight-left">
          <ObjectIcon kind={icon} size="lg" />
          <div className="lightning-highlight-text">
            {title !== undefined && (
              <h1 className="lightning-title">{title}</h1>
            )}
            {helper !== undefined && (
              <p
                className="muted"
                style={{
                  margin: '6px 0 0',
                  fontSize: 13,
                  maxWidth: '72ch',
                  lineHeight: 1.45,
                }}
              >
                {helper}
              </p>
            )}
          </div>
        </div>
        {rightCluster !== null && (
          <div
            className="lightning-highlight-actions"
            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
          >
            {rightCluster}
          </div>
        )}
      </div>
    );
  }

  // Embedded (or standalone without icon) → section-title header. Left =
  // title (+ count badge) + helper, right = merged right cluster. Both
  // sides vertically centered so a two-line helper still lines up with a
  // compact search + filter cluster.
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 10,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {title !== undefined && (
          <h2 className="section-title" style={{ marginTop: 0, marginBottom: 4 }}>
            {title}
            {count !== undefined && total !== undefined && count < total && (
              <span
                className="muted"
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  fontWeight: 400,
                }}
              >
                {count} of {total}
              </span>
            )}
          </h2>
        )}
        {helper !== undefined && (
          <p
            className="muted"
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.5,
              maxWidth: '68ch',
            }}
          >
            {helper}
          </p>
        )}
      </div>
      {rightCluster !== null && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          {rightCluster}
        </div>
      )}
    </div>
  );
}

function ListViewShellSearchInput({ search }: { search: ListViewShellSearchConfig }) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 8,
          display: 'inline-flex',
          color: 'var(--fg-muted)',
          pointerEvents: 'none',
        }}
      >
        <IconSearch size={14} />
      </span>
      <input
        type="search"
        className="form-input"
        placeholder={search.placeholder ?? 'Search'}
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
        aria-label={search.ariaLabel ?? search.placeholder ?? 'Search'}
        style={{
          paddingLeft: 28,
          minWidth: 200,
          maxWidth: 300,
          fontSize: 13,
        }}
      />
    </div>
  );
}

interface ListViewShellBulkBarProps {
  bulkSelect: ListViewShellBulkSelectConfig;
  variant: 'standalone' | 'embedded';
}

function ListViewShellBulkBar({ bulkSelect, variant: _variant }: ListViewShellBulkBarProps) {
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '10px 12px',
        marginBottom: 10,
        borderRadius: 6,
        border: '1px solid var(--border)',
        background: 'var(--olytic-surface)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
      }}
    >
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--olytic-obsidian)',
        }}
      >
        {bulkSelect.selectedIds.size} selected
      </span>
      {bulkSelect.progressCaption !== undefined && (
        <span
          className="muted"
          aria-live="polite"
          style={{ fontSize: 12 }}
        >
          {bulkSelect.progressCaption}
        </span>
      )}
      {bulkSelect.extraContent !== undefined && bulkSelect.extraContent}
      {bulkSelect.actions.map((action, idx) => (
        <button
          key={`${action.label}-${idx}`}
          type="button"
          className="btn btn-secondary"
          onClick={action.onClick}
          disabled={action.disabled === true}
          style={{
            fontSize: 12.5,
            ...(action.danger === true ? { color: 'var(--danger, #b91c1c)' } : {}),
          }}
        >
          {action.label}
        </button>
      ))}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => bulkSelect.onSelectionChange(new Set())}
        style={{ marginLeft: 'auto', fontSize: 12.5 }}
      >
        Cancel
      </button>
    </div>
  );
}

interface ListViewShellSortControlsProps {
  sort: ListViewShellSortConfig;
}

function ListViewShellSortControls({ sort }: ListViewShellSortControlsProps) {
  if (sort.mode !== 'controlled') return null;
  if (sort.onSortChange === undefined) return null;
  const onSortChange = sort.onSortChange;
  const activeSort = sort.activeSort;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 10,
        fontSize: 12,
        color: 'var(--fg-muted)',
      }}
    >
      <span>Sort by:</span>
      {sort.columns.map((col) => {
        const isActive = activeSort?.columnId === col.id;
        const nextDir: 'asc' | 'desc' =
          isActive && activeSort?.direction === 'asc' ? 'desc' : 'asc';
        return (
          <button
            key={col.id}
            type="button"
            className={
              isActive ? 'btn btn-secondary btn-sm' : 'btn btn-ghost btn-sm'
            }
            onClick={() => onSortChange({ columnId: col.id, direction: nextDir })}
            style={{ fontSize: 12 }}
          >
            {col.label}
            {isActive && (activeSort?.direction === 'asc' ? ' ↑' : ' ↓')}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Push 82 (2026-07-23) refinement A.3 — active-facet chip strip.
// Renders below the toolbar when the consumer supplies `filter.activeFacets`.
// Replaces the verbose "Filtered by X, sorted by Y" status text (Salesforce
// anti-pattern) with a compact inline chip row + "Clear all" affordance.
// ---------------------------------------------------------------------------

interface ListViewShellActiveFacetsRowProps {
  facets: ListViewShellActiveFacet[];
  onReset?: () => void;
}

function ListViewShellActiveFacetsRow({
  facets,
  onReset,
}: ListViewShellActiveFacetsRowProps) {
  return (
    <div
      role="region"
      aria-label="Active filters"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        marginBottom: 10,
      }}
    >
      {facets.map((facet) => (
        <span
          key={facet.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 4px 3px 8px',
            fontSize: 12,
            color: 'var(--olytic-obsidian)',
            background: 'var(--olytic-obsidian-08)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            lineHeight: 1.4,
          }}
        >
          <span>{facet.label}</span>
          <button
            type="button"
            className="btn-ghost"
            aria-label={`Remove filter ${facet.label}`}
            onClick={facet.onRemove}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              padding: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              color: 'var(--fg-muted)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--border)';
              e.currentTarget.style.color = 'var(--olytic-obsidian)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--fg-muted)';
            }}
          >
            <IconClose size={10} />
          </button>
        </span>
      ))}
      {facets.length >= 2 && onReset !== undefined && (
        <button
          type="button"
          onClick={onReset}
          style={{
            fontSize: 12,
            color: 'var(--olytic-teal)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '3px 6px',
            marginLeft: 2,
          }}
        >
          Clear all
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Push 82 (2026-07-23) refinement A.2 — column header sort chevrons.
//
// `<SortableColumnHeader>` replaces a plain `<th>` in the consumer's `<thead>`.
// The header renders label + chevron icon that reflects current sort state:
//   - inactive: chevron-up-down (both directions, muted)
//   - active asc: chevron-up (teal)
//   - active desc: chevron-down (teal)
// Click cycles: none → asc → desc → none.
//
// The `useListViewShellSortState<TId>()` companion hook manages the tri-state
// with localStorage persistence (via `useListViewShellStorage`). Consumers
// wire the `activeSort` + `onSortChange` from the hook into each header cell.
//
// Sort application is caller-owned (via `useListViewShellClientSort` for
// client-side or a fetch-effect for controlled). Sort chevrons are pure UI
// affordance; consumers who prefer the button-bar shape from Push 81 can
// still opt into the `sort` prop mode='controlled' — both surfaces coexist.
// ---------------------------------------------------------------------------

export interface SortableColumnHeaderProps<TId extends string = string>
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'onClick' | 'children'> {
  /** Column identifier — must match the `columnId` used in `activeSort`. */
  columnId: TId;
  /** Visible label rendered before the chevron icon. */
  label: ReactNode;
  /** Current sort tuple — `undefined` when no column is active. */
  activeSort: ListViewShellSortActive<TId> | undefined;
  /**
   * Click handler. Consumer typically wires this to a state setter (or the
   * `onSortChange` from `useListViewShellSortState`). Called with the next
   * sort tuple, or `undefined` when cycling back to "no sort".
   */
  onSortChange: (next: ListViewShellSortActive<TId> | undefined) => void;
  /**
   * When true, chevron sits on the LEFT of the label (default: right). Used
   * for numeric columns where the value is right-aligned + chevron reads
   * more naturally next to the label.
   */
  chevronOnLeft?: boolean;
  /**
   * Optional style override — merged with the primitive's sortable-header
   * chrome. Common uses: `textAlign: 'right'` for numeric columns.
   */
  style?: CSSProperties;
}

export function SortableColumnHeader<TId extends string = string>({
  columnId,
  label,
  activeSort,
  onSortChange,
  chevronOnLeft = false,
  style,
  ...thProps
}: SortableColumnHeaderProps<TId>) {
  const isActive = activeSort?.columnId === columnId;
  const direction: 'asc' | 'desc' | undefined = isActive ? activeSort?.direction : undefined;

  function handleClick(): void {
    if (!isActive) {
      onSortChange({ columnId, direction: 'asc' });
    } else if (direction === 'asc') {
      onSortChange({ columnId, direction: 'desc' });
    } else {
      onSortChange(undefined);
    }
  }

  const chevron = (() => {
    if (direction === 'asc') {
      return (
        <IconChevron
          size={12}
          direction="up"
          style={{ color: 'var(--olytic-teal)' }}
        />
      );
    }
    if (direction === 'desc') {
      return (
        <IconChevron
          size={12}
          direction="down"
          style={{ color: 'var(--olytic-teal)' }}
        />
      );
    }
    // Inactive — draw both chevrons stacked, muted. Represents the
    // "sortable, not currently sorting" affordance.
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-muted)',
          opacity: 0.55,
          lineHeight: 0.5,
        }}
      >
        <IconChevron size={9} direction="up" />
        <IconChevron size={9} direction="down" />
      </span>
    );
  })();

  return (
    <th
      {...thProps}
      onClick={handleClick}
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        ...(style ?? {}),
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          justifyContent: style?.textAlign === 'right' ? 'flex-end' : 'flex-start',
        }}
      >
        {chevronOnLeft && chevron}
        <span>{label}</span>
        {!chevronOnLeft && chevron}
      </span>
    </th>
  );
}

/**
 * Push 82 (2026-07-23) refinement A.2 — sort-state hook with localStorage
 * persistence. Wraps `useListViewShellStorage` with a validator that admits
 * either `undefined` (no sort) OR `{ columnId, direction }`.
 *
 * Suffix defaults to `'sort'`; full localStorage key becomes
 * `${storageKey}:sort` (matches the shell's per-surface namespace).
 *
 * Usage:
 *
 *   const [activeSort, setActiveSort] = useListViewShellSortState<'name'|'runs'>(
 *     'list-view:admin-people',
 *     undefined,
 *   );
 *   const sorted = useListViewShellClientSort(rows, activeSort, (row, id) =>
 *     id === 'runs' ? row.runs_count : row.display_name.toLowerCase(),
 *   );
 *   // Then in the thead:
 *   <SortableColumnHeader columnId="name" label="Name"
 *     activeSort={activeSort} onSortChange={setActiveSort} />
 */
export function useListViewShellSortState<TId extends string = string>(
  storageKey: string,
  initial: ListViewShellSortActive<TId> | undefined = undefined,
  suffix: string = 'sort',
): [
  ListViewShellSortActive<TId> | undefined,
  (next: ListViewShellSortActive<TId> | undefined) => void,
] {
  function isValid(raw: unknown): raw is ListViewShellSortActive<TId> | undefined {
    if (raw === null) return true;
    if (typeof raw !== 'object' || raw === undefined) return false;
    const obj = raw as Record<string, unknown>;
    if (typeof obj.columnId !== 'string') return false;
    if (obj.direction !== 'asc' && obj.direction !== 'desc') return false;
    return true;
  }
  const [rawState, setRawState] = useListViewShellStorage<
    ListViewShellSortActive<TId> | null
  >(
    storageKey,
    suffix,
    initial === undefined ? null : initial,
    isValid as (raw: unknown) => raw is ListViewShellSortActive<TId> | null,
  );
  const state: ListViewShellSortActive<TId> | undefined =
    rawState === null ? undefined : rawState;
  const setState = useCallback(
    (next: ListViewShellSortActive<TId> | undefined) => {
      setRawState(next === undefined ? null : next);
    },
    [setRawState],
  );
  return [state, setState];
}

// ---------------------------------------------------------------------------
// Utility hook — client-side sort helper (opt-in). Consumers with
// `sortMode='client'` can wire this into their `useMemo` if they don't want
// to hand-roll comparator + toggle logic.
// ---------------------------------------------------------------------------

export function useListViewShellClientSort<TRow, TId extends string = string>(
  rows: TRow[] | null,
  activeSort: ListViewShellSortActive<TId> | undefined,
  compareBy: (row: TRow, columnId: TId) => number | string | null | undefined,
): TRow[] | null {
  return useMemo(() => {
    if (rows === null) return null;
    if (activeSort === undefined) return rows;
    const direction = activeSort.direction === 'asc' ? 1 : -1;
    const copied = [...rows];
    copied.sort((a, b) => {
      const va = compareBy(a, activeSort.columnId);
      const vb = compareBy(b, activeSort.columnId);
      if (va === vb) return 0;
      if (va === null || va === undefined) return direction;
      if (vb === null || vb === undefined) return -direction;
      return va < vb ? -direction : direction;
    });
    return copied;
  }, [rows, activeSort, compareBy]);
}

// ---------------------------------------------------------------------------
// Utility hook — surface-scoped localStorage helper. Reads + writes a JSON
// blob under the shell's `storageKey` convention. Opt-in; some consumers
// have surface-specific key shapes (multi-key JSON blobs) and manage
// persistence themselves. This helper covers the common single-JSON case.
// ---------------------------------------------------------------------------

export function useListViewShellStorage<T>(
  storageKey: string,
  suffix: string,
  initial: T,
  isValid: (raw: unknown) => raw is T,
): [T, (next: T) => void] {
  const fullKey = `${storageKey}:${suffix}`;
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(fullKey);
      if (raw === null) return initial;
      const parsed: unknown = JSON.parse(raw);
      if (isValid(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return initial;
  });
  function update(next: T): void {
    setState(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(fullKey, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }
  return [state, update];
}
